import { useEffect, useRef } from "react";
import { usePathname } from "wouter/use-browser-location";

import {
  pathTemplate,
  registerAcquisition,
  setPathTemplate,
  slugify,
  track,
} from "@/lib/analytics";
import { LIVE_SPECIALTY_ROUTE_SLUGS } from "@/client-route-slugs";
import { classifyReferrer } from "@/lib/referrer-class";

// `usePathname` comes from wouter's browser-location module rather than the
// `useLocation` exported from "wouter" on purpose: it works OUTSIDE the
// <Router> (this hook is mounted by App.client.tsx, which wraps <App/> rather
// than living inside it) and it subscribes to the same patched history the
// router itself uses, so a <Link> navigation and a back button both land here.
//
// This file is only ever reached from App.client.tsx, which the server never
// renders — App.server.tsx is the prerender entry — so there is no SSR path.

/**
 * The host of the referring page, or a coarse label. Only the HOST: a referrer
 * URL can carry a search query, and that is exactly the free text this contract
 * refuses to send.
 *
 * `document.referrer` does not change on a pushState navigation, so on every
 * page_view after the first this is still the host the visitor ARRIVED from —
 * which is the useful answer for a funnel, not a bug.
 */
function referrerHost(): string {
  try {
    const ref = document.referrer;
    if (!ref) return "direct";
    const host = new URL(ref).host;
    if (!host) return "direct";
    return host === window.location.host ? "internal" : host;
  } catch {
    return "unknown";
  }
}

/**
 * The campaign source, bounded. `utm_source` is the one property on this site
 * whose raw value a visitor controls (it is in their URL), so it is slugified
 * to lowercase-and-dashes at 48 characters before it can reach a breakdown
 * chart, and a visit with no campaign says "none" rather than nothing — an
 * absent property and a direct visit are different answers.
 */
function utmSource(): string {
  try {
    const raw = new URLSearchParams(window.location.search).get("utm_source");
    return raw ? slugify(raw) : "none";
  } catch {
    return "none";
  }
}

/**
 * THE PAGES THAT ENTER THE FUNNEL AT THE TOP.
 *
 * `landing_view` is the funnel's first step, so it may only fire on a page that
 * IS a top of funnel. "/" is the neutral root; "/sucks" is the share page, whose
 * whole job is to be the first thing a visitor arriving from a social caption
 * sees (src/pages/sucks.tsx). Each live specialty page ("/anesthesia",
 * "/neurosurgery") is where paid social lands: before 2026-10-13 instagram-ads
 * sent 132 visitors there and logged 1 `landing_view` (E-484). Every one of
 * them carries the same repeated primary CTA, the store badge (LUX-19), so the
 * step below this one means the same thing on each.
 *
 * Adding a template here without a `landing_view` step to match it in
 * scripts/posthog-web-funnel.mjs would merge two funnels into one number: the
 * "/" funnel there filters its first step on `path_template`, and the
 * specialty pages have their own.
 */
const LANDING_TEMPLATES = new Set<string>([
  "/",
  "/sucks",
  ...LIVE_SPECIALTY_ROUTE_SLUGS.map((slug) => `/${slug}`),
]);

/**
 * Fires `page_view` on first render and on every wouter route change, keeps
 * `path_template` registered as a super property, and fires `landing_view`
 * once per page load on a landing page — "/", "/sucks" or a live specialty
 * page (see LANDING_TEMPLATES).
 *
 * It also registers WHERE THIS VISIT CAME FROM — `referrer_host`, `utm_source`
 * and `referrer_class` — as super properties on the first page view, so every
 * later event carries them. That is what makes "waitlist_complete broken down
 * by referrer" a chart at all: without it, acquisition would only be attached
 * to the page_view at the top of the visit and could never be read off the
 * conversion at the bottom of it.
 *
 * Returns the current path template so the caller can key other page-scoped
 * measurement (scroll depth) off the same value.
 */
export function usePageView(): string {
  const pathname = usePathname();
  const template = pathTemplate(pathname);
  const lastPath = useRef<string | null>(null);
  const landingFired = useRef(false);
  const acquisitionRegistered = useRef(false);

  useEffect(() => {
    // Guard on the raw path, not the template: /help/a → /help/b are two page
    // views that share one template, and both should be counted.
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;

    setPathTemplate(template);

    // Once per page load, before the first page_view is sent, so the very first
    // event already carries it.
    if (!acquisitionRegistered.current) {
      acquisitionRegistered.current = true;
      const host = referrerHost();
      const source = utmSource();
      registerAcquisition({
        referrer_host: host,
        utm_source: source,
        // The bucket, not the host: six values a chart can actually be broken
        // down by, and the only one that answers "did an assistant naming us
        // ever send anyone" (docs/ops/ai-visibility.md). `utm_source` is
        // already slugified here — classifyReferrer normalises both spellings.
        referrer_class: classifyReferrer({
          referrerHost: host,
          utmSource: source,
        }),
      });
    }

    track("page_view", {
      path_template: template,
      referrer_host: referrerHost(),
    });

    // Once per page load. Returning to a landing page via client-side
    // navigation must not re-enter the funnel at the top, or every step below it
    // reads as a drop.
    if (LANDING_TEMPLATES.has(template) && !landingFired.current) {
      landingFired.current = true;
      track("landing_view");
    }
  }, [pathname, template]);

  return template;
}
